import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Button, Modal, Form } from "react-bootstrap";
import { siteLogo as logo } from "../../const";
import NavbarWithDrawer from "../../ComponentsWeb/Sub/NavbarWithDrawer";
import Footer from "../../ComponentsWeb/Sub/Footer";
import { chat } from "../../const";
import { socketEmit, socketOn } from "../../Socket";
import moment from "moment";
import socket from "../../socketInstance";
import { allApi, postApi, postApiFile } from "../../helpers/apiStructure";
import LinkifiedText from "../../Components/LinkifiedText";
import supportIcon from "../../images/customer-care-247.png";
import { parseBannerSlides } from "../../helpers/bannerSlides";

const TOP_BANNER_SLIDE_INTERVAL_MS = 4000;
// Must match the transition duration on .customer-banner-track in app.css.
const TOP_BANNER_SLIDE_TRANSITION_MS = 1200;

const customerBannerDefaults = {
  customer_top_banner_text: "",
  customer_top_banner_text_url: "",
  customer_marquee_text: "",
  customer_bottom_banner_text: "",
  customer_bottom_banner_text_url: "",
  customer_support_link: "",
};

const normalizeBannerUrl = (url = "") => {
  const trimmedUrl = String(url || "").trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const getUserRowId = (item) => item?.userId || item?.sender || item?.id || "";

const mergeUsersById = (previousUsers, incomingUsers) => {
  const mergedUsers = new Map();

  previousUsers.forEach((user) => {
    const userId = getUserRowId(user);
    if (userId) {
      mergedUsers.set(userId, user);
    }
  });

  incomingUsers.forEach((user) => {
    const userId = getUserRowId(user);
    if (!userId) {
      return;
    }

    const existingUser = mergedUsers.get(userId) || {};
    mergedUsers.set(userId, {
      ...existingUser,
      ...user,
      lastMessage: user?.lastMessage || existingUser?.lastMessage,
    });
  });

  return Array.from(mergedUsers.values());
};

const getMessageTypePreview = (messageType) => {
  switch (messageType) {
    case "image":
      return "Photo";
    case "sticker":
      return "Sticker";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "file":
      return "Attachment";
    default:
      return "";
  }
};

const getChatPreviewText = (message, maxLength = 60) => {
  const content = String(message?.content || "")
    .replace(/\s+/g, " ")
    .trim();

  const fallbackPreview = getMessageTypePreview(message?.messageType);
  const previewText = content || fallbackPreview;

  if (!previewText) {
    return "";
  }

  if (previewText.length <= maxLength) {
    return previewText;
  }

  return `${previewText.slice(0, maxLength).trim()}...`;
};

const CHAT_NAV_KEY = "chatUsersPaginationState";
// How long a touch has to be held before the archive menu opens.
const LONG_PRESS_DURATION_MS = 450;
const CHAT_MENU_WIDTH = 190;
// Roughly the rendered menu size, used to keep it inside the viewport.
// Bump this if menu items are added or removed.
const CHAT_MENU_HEIGHT = 110;
let pendingNavRestore = false;
// groupId -> id of the last message that was already on screen when the group
// was opened. Storing the message id (rather than just "this group was visited")
// means a message arriving afterwards still counts as unread.
const visitedGroupLastSeen = new Map();

const ChatUsers = ({ socketReady }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRestoredRef = useRef(false);
  const skipNextResetRef = useRef(false);

  useEffect(() => {
    pendingNavRestore = false;
  }, []);

  const queryParameters = new URLSearchParams(window.location.search);
  let agentList = queryParameters.get("agent");
  const shouldOpenCreateGroup = queryParameters.get("createGroup") === "true";

  const getInitialPage = () => {
    if (!pendingNavRestore) return 1;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.page) {
        skipNextResetRef.current = true;
        return saved.page;
      }
    } catch {}
    return 1;
  };

  const getInitialTab = () => {
    if (!pendingNavRestore) return "all";
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.tab) return saved.tab;
    } catch {}
    return "all";
  };

  const getInitialArchivedView = () => {
    if (!pendingNavRestore) return false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      return !!saved?.archived;
    } catch {}
    return false;
  };

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(getInitialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [groupTopic, setGroupTopic] = useState("");
  const [groupImage, setGroupImage] = useState(null);
  const [groupImagePreview, setGroupImagePreview] = useState("");
  const [editingGroupId, setEditingGroupId] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showArchived, setShowArchived] = useState(getInitialArchivedView);
  const [archivedGroupIds, setArchivedGroupIds] = useState(() => new Set());
  const [pinnedGroupIds, setPinnedGroupIds] = useState(() => new Set());
  const [chatMenu, setChatMenu] = useState(null);
  const [customerBannerSettings, setCustomerBannerSettings] = useState(
    customerBannerDefaults
  );
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  const [topBannerTransitionEnabled, setTopBannerTransitionEnabled] = useState(true);
  const userSlug = localStorage.getItem("slug");
  const isCustomer = userSlug === "customer";
  const canCreateGroup = (userSlug === "team_leader" || userSlug === "branch_leader");
  const canDeleteGroup = userSlug === "admin";
  const canManageGroups = canCreateGroup || canDeleteGroup;
  const legacyTopBannerUrl = normalizeBannerUrl(
    customerBannerSettings?.customer_top_banner_text_url
  );
  const bottomBannerUrl = normalizeBannerUrl(
    customerBannerSettings?.customer_bottom_banner_text_url
  );
  const supportLinkUrl = normalizeBannerUrl(
    customerBannerSettings?.customer_support_link
  );
  const topBannerSlides = parseBannerSlides(
    customerBannerSettings?.customer_top_banner_text
  ).map((slide, index) =>
    index === 0 && !slide.link && legacyTopBannerUrl
      ? { ...slide, link: legacyTopBannerUrl }
      : slide
  );
  // A trailing clone of the first slide lets the track keep moving left
  // continuously; once it lands on the clone we snap back to index 0 with
  // the transition disabled, so it never animates backwards through all slides.
  const topBannerLoopSlides =
    topBannerSlides.length > 1
      ? [...topBannerSlides, topBannerSlides[0]]
      : topBannerSlides;
  const topBannerActiveDot =
    topBannerSlides.length > 0 ? topBannerIndex % topBannerSlides.length : 0;

  // Refs (not state) so the timer helpers below are stable and never see a
  // stale slide count, even when called from the visibilitychange listener.
  const topBannerTimerRef = useRef(null);
  const topBannerSlidesLenRef = useRef(topBannerSlides.length);
  topBannerSlidesLenRef.current = topBannerSlides.length;

  const stopTopBannerTimer = useRef(() => {
    clearInterval(topBannerTimerRef.current);
    topBannerTimerRef.current = null;
  });

  const startTopBannerTimer = useRef(() => {
    stopTopBannerTimer.current();
    if (topBannerSlidesLenRef.current <= 1) return;
    topBannerTimerRef.current = setInterval(() => {
      setTopBannerTransitionEnabled(true);
      setTopBannerIndex((prev) => prev + 1);
    }, TOP_BANNER_SLIDE_INTERVAL_MS);
  });

  const resetTopBannerSlider = useRef(() => {
    stopTopBannerTimer.current();
    setTopBannerTransitionEnabled(false);
    setTopBannerIndex(0);
    // Timer restarts inside the !topBannerTransitionEnabled effect below,
    // once the no-transition frame has actually painted.
  });

  useEffect(() => {
    setTopBannerIndex(0);
    setTopBannerTransitionEnabled(true);
  }, [customerBannerSettings?.customer_top_banner_text]);

  // Start/restart the auto-advance timer whenever the slide count changes.
  useEffect(() => {
    startTopBannerTimer.current();
    return () => stopTopBannerTimer.current();
  }, [topBannerSlides.length]);

  // Snap back to slide 0 via setTimeout rather than onTransitionEnd once the
  // track reaches the cloned first slide: onTransitionEnd never fires in a
  // hidden tab (nothing is rendering), but setTimeout still does.
  useEffect(() => {
    if (topBannerSlides.length <= 1) return undefined;
    if (topBannerIndex !== topBannerSlides.length) return undefined;

    const resetTimer = setTimeout(() => {
      setTopBannerTransitionEnabled(false);
      setTopBannerIndex(0);
    }, TOP_BANNER_SLIDE_TRANSITION_MS);

    return () => clearTimeout(resetTimer);
  }, [topBannerIndex, topBannerSlides.length]);

  // Re-enable the transition only after the transition-disabled frame has
  // actually painted (double rAF), then resume auto-advance. This is the
  // single place that turns the transition back on, whether we got here
  // from a normal loop reset or from resetTopBannerSlider on tab return.
  useEffect(() => {
    if (topBannerTransitionEnabled) return undefined;

    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setTopBannerTransitionEnabled(true);
        startTopBannerTimer.current();
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
    };
  }, [topBannerTransitionEnabled]);

  // setInterval keeps firing (throttled) in a hidden tab, which would let
  // the index drift far past the slide count with nothing to catch it since
  // rendering (and rAF) is suspended too. Stop the timer while hidden, and
  // do a clean, un-animated reset to slide 0 when the tab becomes visible.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        stopTopBannerTimer.current();
      } else {
        resetTopBannerSlider.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  if(!agentList && userSlug === "branch_leader") {
    agentList = "true";
  }
  
  

  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     try {
  //       const response = await socketEmit("users_chat_with_me", {
  //         agentask: agentList === "true" ? 1 : 0,
  //         page,
  //       });
  //       console.log("✅ users_chat_with_me", response);
  //       setUsers(response?.data || []);
  //     } catch (err) {
  //       console.error("Socket emit error:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   if (socketReady) {
  //     fetchUsers();
  //   }
  // }, [socketId, socketReady, location]);
  useEffect(() => {
    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }
    sessionStorage.removeItem(CHAT_NAV_KEY);
    scrollRestoredRef.current = false;
    setUsers([]);
    setPage(1);
    setTotalPages(0);
    setActiveTab("all");
    setShowArchived(false);
  }, [location]);

  useEffect(() => {
    if (canCreateGroup && shouldOpenCreateGroup) {
      openGroupModal();
      const nextQueryParameters = new URLSearchParams(location.search);
      nextQueryParameters.delete("createGroup");
      navigate(
        `${location.pathname}${nextQueryParameters.toString() ? `?${nextQueryParameters.toString()}` : ""}`,
        { replace: true }
      );
    }
  }, [canCreateGroup, shouldOpenCreateGroup, location.pathname, location.search, navigate]);

  useEffect(() => {
    const fetchCustomerBannerSettings = async () => {
      try {
        const response = await postApi(
          `${baseUrl}/api/settings/forcustomer`,
          "post",
          { filter: "customer_banner" }
        );
        setCustomerBannerSettings({
          ...customerBannerDefaults,
          ...(response?.setting || {}),
        });
      } catch (error) {
        console.error("Customer banner settings error:", error);
        setCustomerBannerSettings(customerBannerDefaults);
      }
    };

    if (isCustomer) {
      fetchCustomerBannerSettings();
    }
  }, [baseUrl, isCustomer]);

  const fetchGroups = async () => {
    try {
      const response = await allApi(`${baseUrl}/api/chat-groups`, "get");
      const myId = localStorage.getItem("userid");
      const data = (response?.data || []).map((group) => {
        if (!group.lastMessage || !visitedGroupLastSeen.has(group?.id)) return group;
        // Only suppress the unread flag for the exact message that had already
        // been seen. A newer last message must stay unread, otherwise opening
        // the Archived folder would silently clear its unread indicator.
        const seenMessageId = visitedGroupLastSeen.get(group.id);
        const currentMessageId = String(
          group.lastMessage.id || group.lastMessage._id || ""
        );
        if (!seenMessageId || seenMessageId !== currentMessageId) return group;
        const readBy = group.lastMessage.readBy || [];
        const alreadyIn = readBy.some((id) => String(id?.id || id?._id || id) === myId);
        if (alreadyIn) return group;
        return { ...group, lastMessage: { ...group.lastMessage, readBy: [...readBy, myId] } };
      });
      setGroups(data);
      setArchivedGroupIds(
        new Set(data.filter((group) => group?.isArchived).map((group) => group?.id))
      );
      setPinnedGroupIds(
        new Set(data.filter((group) => group?.isPinned).map((group) => group?.id))
      );
    } catch (error) {
      console.error("Group list error:", error);
      setGroups([]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await socketEmit("users_chat_with_me", {
          agentask: agentList === "true" ? 1 : 0,
          page,
          filter: activeTab,
        });

        const newUsers = response?.data || [];
        setUsers(newUsers);
        if (response?.total != null && response?.pageSize) {
          setTotalPages(Math.ceil(response.total / response.pageSize));
        }

      } catch (err) {
        console.error("Socket emit error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (socketReady) {
      fetchUsers();
      fetchGroups();
    }
  }, [socketReady, page, agentList, location, activeTab, showArchived]);

  // Restore scroll position after back-navigation fetch completes
  useEffect(() => {
    if (scrollRestoredRef.current || !users.length) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CHAT_NAV_KEY));
      if (saved?.scrollY) {
        setTimeout(() => window.scrollTo({ top: saved.scrollY, behavior: "instant" }), 60);
        scrollRestoredRef.current = true;
      }
    } catch {}
  }, [users]);

  const saveNavState = () => {
    try {
      pendingNavRestore = true;
      sessionStorage.setItem(
        CHAT_NAV_KEY,
        JSON.stringify({ page, scrollY: window.scrollY, tab: activeTab, archived: showArchived })
      );
    } catch {}
  };

  const openGroupModal = async () => {
    setShowGroupModal(true);
    setGroupLoading(true);
    setEditingGroupId("");
    setGroupName("");
    setGroupTopic("");
    setGroupImage(null);
    setGroupImagePreview("");
    setSelectedMembers([]);

    try {
      const response = await postApi(`${baseUrl}/api/teamleaders`, "post", {
        page: 1,
      });

      const availableUsers = (response?.list || []).filter(
        (user) => user?.id !== localStorage.getItem("userid") && user?.status !== false
      );

      setGroupUsers(availableUsers);
    } catch (error) {
      console.error("Group users fetch error:", error);
      setGroupUsers([]);
    } finally {
      setGroupLoading(false);
    }
  };

  const closeGroupModal = () => {
    setShowGroupModal(false);
    setGroupUsers([]);
    setGroupName("");
    setGroupTopic("");
    setGroupImage(null);
    setGroupImagePreview("");
    setEditingGroupId("");
    setSelectedMembers([]);
    setCreatingGroup(false);
  };

  const toggleGroupMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const saveGroup = async () => {
    if (!groupName.trim()) {
      return;
    }

    setCreatingGroup(true);

    try {
      const response = await postApi(
        editingGroupId
          ? `${baseUrl}/api/chat-group/edit/${editingGroupId}`
          : `${baseUrl}/api/chat-group/add`,
        editingGroupId ? "put" : "post",
        {
        name: groupName,
        topic: groupTopic,
        members: selectedMembers,
        }
      );

      if (response?.status === 1) {
        const savedGroupId = response?.data?.id || editingGroupId;
        if (groupImage && savedGroupId) {
          await postApiFile(
            `${baseUrl}/api/chat-group/image/${savedGroupId}`,
            "put",
            { image: groupImage }
          );
        }
        await fetchGroups();
        closeGroupModal();
      }
    } catch (error) {
      console.error("Save group error:", error);
    } finally {
      setCreatingGroup(false);
    }
  };

  const openEditGroupModal = async (group) => {
    setShowGroupModal(true);
    setGroupLoading(true);
    setEditingGroupId(group?.id || "");
    setGroupName(group?.name || "");
    setGroupTopic(group?.topic || "");
    setGroupImage(null);
    setGroupImagePreview(group?.image || "");
    setSelectedMembers(group?.members || []);

    try {
      const response = await postApi(`${baseUrl}/api/teamleaders`, "post", {
        page: 1,
      });

      const availableUsers = (response?.list || []).filter(
        (user) => user?.id !== localStorage.getItem("userid") && user?.status !== false
      );

      setGroupUsers(availableUsers);
    } catch (error) {
      console.error("Group users fetch error:", error);
      setGroupUsers([]);
    } finally {
      setGroupLoading(false);
    }
  };

  const deleteGroup = async (group) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${group?.name || "this group"}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await allApi(`${baseUrl}/api/chat-group/${group?.id}`, "delete");

      if (response?.status === 1) {
        setGroups((prevGroups) =>
          prevGroups.filter((item) => item?.id !== group?.id)
        );
      }
    } catch (error) {
      console.error("Delete group error:", error);
    }
  };

  // --- Archive (WhatsApp style) -------------------------------------------
  // A long press (touch) or right click (desktop) on any chat row opens a small
  // menu with Archive/Unarchive. The press must not also fire the row's
  // navigate-to-chat click, hence the ref guard below.
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const closeChatMenu = () => {
    setChatMenu(null);
    longPressFiredRef.current = false;
  };

  const openChatMenu = (point, target) => {
    // Keep the menu fully inside the viewport, whichever corner was pressed.
    const left = Math.min(
      Math.max(point.clientX, 8),
      Math.max(window.innerWidth - CHAT_MENU_WIDTH - 8, 8)
    );
    const top = Math.min(
      Math.max(point.clientY, 8),
      Math.max(window.innerHeight - CHAT_MENU_HEIGHT - 8, 8)
    );
    setChatMenu({ ...target, left, top });
  };

  const getChatMenuProps = (target) => ({
    onContextMenu: (event) => {
      event.preventDefault();
      longPressFiredRef.current = true;
      openChatMenu(event, target);
    },
    onTouchStart: (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      const point = { clientX: touch.clientX, clientY: touch.clientY };
      longPressFiredRef.current = false;
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        longPressFiredRef.current = true;
        openChatMenu(point, target);
      }, LONG_PRESS_DURATION_MS);
    },
    onTouchMove: () => clearTimeout(longPressTimerRef.current),
    onTouchEnd: () => clearTimeout(longPressTimerRef.current),
    onTouchCancel: () => clearTimeout(longPressTimerRef.current),
  });

  // Swallows the click the browser fires right after a long press.
  const consumedByLongPress = () => {
    if (!longPressFiredRef.current) return false;
    longPressFiredRef.current = false;
    return true;
  };

  useEffect(() => () => clearTimeout(longPressTimerRef.current), []);

  useEffect(() => {
    if (!chatMenu) return undefined;

    const handleDismiss = () => closeChatMenu();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeChatMenu();
    };

    window.addEventListener("scroll", handleDismiss, true);
    window.addEventListener("resize", handleDismiss);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("scroll", handleDismiss, true);
      window.removeEventListener("resize", handleDismiss);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [chatMenu]);

  // Archiving applies to groups only — contact chats are never archivable.
  const toggleArchive = async (target) => {
    if (!target?.id) return;

    const nextArchived = !target.archived;
    const applyLocally = (archived) => {
      setArchivedGroupIds((prev) => {
        const next = new Set(prev);
        if (archived) {
          next.add(String(target.id));
        } else {
          next.delete(String(target.id));
        }
        return next;
      });
    };

    closeChatMenu();
    applyLocally(nextArchived);

    try {
      const response = await postApi(`${baseUrl}/api/chat/archive`, "post", {
        targetId: target.id,
        type: "group",
        archive: nextArchived,
      });

      if (response?.status !== 1) {
        applyLocally(!nextArchived);
      }
    } catch (error) {
      console.error("Archive chat error:", error);
      applyLocally(!nextArchived);
    }
  };

  // Pinning applies to groups only, same as archiving.
  const togglePin = async (target) => {
    if (!target?.id) return;

    const nextPinned = !target.pinned;
    const applyLocally = (pinned) => {
      setPinnedGroupIds((prev) => {
        const next = new Set(prev);
        if (pinned) {
          next.add(String(target.id));
        } else {
          next.delete(String(target.id));
        }
        return next;
      });
    };

    closeChatMenu();
    applyLocally(nextPinned);

    try {
      const response = await postApi(`${baseUrl}/api/chat/pin`, "post", {
        targetId: target.id,
        pin: nextPinned,
      });

      if (response?.status !== 1) {
        applyLocally(!nextPinned);
      }
    } catch (error) {
      console.error("Pin chat error:", error);
      applyLocally(!nextPinned);
    }
  };

  const openArchivedView = () => {
    closeChatMenu();
    setShowArchived(true);
    setPage(1);
    setSearchQuery("");
    setShowSearch(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const closeArchivedView = () => {
    closeChatMenu();
    setShowArchived(false);
    setPage(1);
    setSearchQuery("");
    setShowSearch(false);
  };

  useEffect(() => {
    const handleNewMessage = (response) => {
      if (response?.chatGroup) {
        const incomingSenderId = response?.sender?.id || response?.sender?._id || response?.sender;
        const myId = localStorage.getItem("userid");
        const readBy = response.readBy || [];
        const isMine = String(incomingSenderId) === myId;
        const readByWithMe = isMine && !readBy.some(id => String(id?.id || id?._id || id) === myId)
          ? [...readBy, myId]
          : readBy;

        setGroups((prevGroups) =>
          prevGroups.map((group) =>
            group?.id === response?.chatGroup
              ? { ...group, lastMessage: { ...response, readBy: readByWithMe } }
              : group
          )
        );
        return;
      }

      setUsers((prevUsers) => {
        const senderId =
          typeof response?.sender === "string"
            ? response?.sender
            : response?.sender?.id || response?.sender?._id;

        // Check if sender already exists
        const userIndex = prevUsers.findIndex(
          (user) =>
            user.userId === senderId || user.sender === senderId
        );

        if (userIndex !== -1) {
          const updatedUsers = [...prevUsers];
          const updatedUser = { ...updatedUsers[userIndex] };

          updatedUser.lastMessage = response;

          updatedUsers.splice(userIndex, 1);

          return [updatedUser, ...updatedUsers];
        } else {
          return [
            {
              userId: senderId,
              sender: senderId,
              username: response?.sender?.username || response?.senderName || "New User",
              firstName: response?.sender?.firstName,
              lastName: response?.sender?.lastName,
              profile: response?.sender?.profile || response?.sender?.image || "",
              lastMessage: response,
            },
            ...prevUsers,
          ];
        }
      });
    };

    socketOn("new_message", handleNewMessage);
    return () => socket.off("new_message", handleNewMessage);
  }, []);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const [dbMessageMatches, setDbMessageMatches] = useState([]);
  const [dbContactMatches, setDbContactMatches] = useState([]);
  const [dbSearchLoading, setDbSearchLoading] = useState(false);

  // DB contact search — name + phone from server (works regardless of socket data)
  useEffect(() => {
    if (!normalizedSearch) {
      setDbContactMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await postApi(
          `${baseUrl}/api/users/search-chatted`,
          "post",
          { searchQuery: normalizedSearch }
        );
        setDbContactMatches(result?.data || []);
      } catch (err) {
        console.error("Contact search error:", err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [normalizedSearch, baseUrl]);

  // DB message content search
  useEffect(() => {
    if (!normalizedSearch) {
      setDbMessageMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      setDbSearchLoading(true);
      try {
        const result = await postApi(
          `${baseUrl}/api/messages/search-all-user-chats`,
          "post",
          { searchQuery: normalizedSearch }
        );
        setDbMessageMatches(result?.data || []);
      } catch (err) {
        console.error("Search all chats error:", err);
      } finally {
        setDbSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [normalizedSearch, baseUrl]);

  // Name-filtered lists (client-side, instant)
  const currentUserId = localStorage.getItem("userid");

  const handleGroupOpen = (group) => {
    if (consumedByLongPress()) return;
    const myId = localStorage.getItem("userid");
    if (group?.id) {
      const seenMessageId = group?.lastMessage?.id || group?.lastMessage?._id;
      visitedGroupLastSeen.set(
        group.id,
        seenMessageId ? String(seenMessageId) : null
      );
    }
    setGroups((prev) =>
      prev.map((g) => {
        if (g?.id !== group?.id || !g.lastMessage) return g;
        const readBy = g.lastMessage.readBy || [];
        const alreadyRead = readBy.some((id) => String(id?.id || id?._id || id) === myId);
        if (alreadyRead) return g;
        return { ...g, lastMessage: { ...g.lastMessage, readBy: [...readBy, myId] } };
      })
    );
    // Remember the page/tab/archived folder so the back arrow returns here
    // rather than resetting to the top of the main chat list.
    saveNavState();
    navigate(
      `${chat}?group=true&groupId=${group?.id}&name=${encodeURIComponent(
        group?.name || "Group"
      )}&topic=${encodeURIComponent(group?.topic || "")}`
    );
  };

  const hasGroupUnread = (group) => {
    const msg = group?.lastMessage;
    if (!msg) return false;
    const senderId = msg.sender?.id || msg.sender?._id || msg.sender;
    if (String(senderId) === currentUserId) return false;
    const readBy = msg.readBy || [];
    return !readBy.some((id) => String(id?.id || id?._id || id) === currentUserId);
  };

  // Newest message first — same as filteredUsers below, so a group jumps
  // to the top of the list as soon as it receives a new message instead of
  // staying wherever it was originally fetched/sorted.
  // Archived groups live in their own folder — the main list hides them and the
  // archived view shows nothing else. Contacts are never archivable.
  const isGroupArchived = (group) => archivedGroupIds.has(group?.id);
  const isGroupPinned = (group) => pinnedGroupIds.has(group?.id);

  const visibleGroups = groups.filter(
    (group) => isGroupArchived(group) === showArchived
  );

  const filteredGroups = (
    normalizedSearch
      ? visibleGroups.filter(
          (g) =>
            (g?.name || "").toLowerCase().includes(normalizedSearch) ||
            (g?.topic || "").toLowerCase().includes(normalizedSearch)
        )
      : visibleGroups
  )
    .slice()
    .sort((a, b) => {
      // Pinned groups always sit on top. Newest-message-first ordering is kept
      // inside each band, so an incoming message still lifts a group to the top
      // of the unpinned list without ever jumping above a pinned one.
      const pinnedDelta = (isGroupPinned(b) ? 1 : 0) - (isGroupPinned(a) ? 1 : 0);
      if (pinnedDelta !== 0) {
        return pinnedDelta;
      }

      const aTime = a?.lastMessage?.createdAt
        ? new Date(a.lastMessage.createdAt).getTime()
        : 0;
      const bTime = b?.lastMessage?.createdAt
        ? new Date(b.lastMessage.createdAt).getTime()
        : 0;
      return bTime - aTime;
    });

  // The archived folder is groups only, so contacts drop out of it entirely.
  const visibleUsers = showArchived ? [] : users;

  const nameFilteredUsers = normalizedSearch
    ? visibleUsers.filter((item) => {
        const name = (
          item?.username ||
          `${item?.firstName || ""} ${item?.lastName || ""}`.trim()
        ).toLowerCase();
        const phone = (item?.phone || "").toLowerCase();
        return name.includes(normalizedSearch) || phone.includes(normalizedSearch);
      })
    : visibleUsers;

  // Merge DB contact matches (by name/phone) that aren't already shown in name filter
  const nameFilteredIds = new Set(nameFilteredUsers.map((u) => u?.userId || u?.sender));
  const extraContactMatches = normalizedSearch
    ? dbContactMatches
        .filter((m) => !nameFilteredIds.has(m.userId) && !showArchived)
        .map((m) => {
          // Try to get full info (lastMessage, lastSeen) from existing users list
          const existing = users.find((u) => (u?.userId || u?.sender) === m.userId);
          return existing ? { ...existing, ...m } : m;
        })
    : [];

  // Combined contacts list — newest message first
  const filteredUsers = [...nameFilteredUsers, ...extraContactMatches].sort((a, b) => {
    const aTime = a?.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
    const bTime = b?.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  // DB message matches — exclude conversations already shown in contacts
  const nameFilteredUserIds = new Set(filteredUsers.map((u) => u?.userId || u?.sender || u?.userId));
  const nameFilteredGroupIds = new Set(filteredGroups.map((g) => g?.id));

  const msgMatchUsers = normalizedSearch
    ? dbMessageMatches.filter(
        (m) => m.type === "user" && !nameFilteredUserIds.has(m.userId) && !showArchived
      )
    : [];
  const msgMatchGroups = normalizedSearch
    ? dbMessageMatches.filter(
        (m) =>
          m.type === "group" &&
          !nameFilteredGroupIds.has(m.groupId) &&
          archivedGroupIds.has(String(m.groupId)) === showArchived
      )
    : [];

  const archivedCount = archivedGroupIds.size;
  // Archived groups track their unread state locally through readBy, which the
  // new_message handler keeps current, so no extra server data is needed.
  const archivedUnreadCount = groups.filter(
    (group) => archivedGroupIds.has(group?.id) && hasGroupUnread(group)
  ).length;

  const hasMessageMatches = msgMatchUsers.length > 0 || msgMatchGroups.length > 0;
  const hasAnyResults =
    filteredGroups.length > 0 || filteredUsers.length > 0 || hasMessageMatches;

  return (
    <>
      <NavbarWithDrawer />
      {isCustomer && customerBannerSettings?.customer_marquee_text && (
        <div className="customer-banner-marquee customer-banner-marquee-header customer-banner-marquee-chat">
          <marquee
            behavior="default"
            className="customer-banner-marquee-track-chat"
            scrollAmount="6"
          >
            <span>{customerBannerSettings.customer_marquee_text}</span>
          </marquee>
        </div>
      )}
      <div className="container-fluid">
        <div className="p-5 mt-2 mb-5 mbl-pedding-10 pt-0">
          {showArchived && (
            <div className="chat-archived-header">
              <button
                type="button"
                className="chat-archived-back"
                onClick={closeArchivedView}
                aria-label="Back to chats"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <span className="chat-archived-title">Archived</span>
            </div>
          )}
          <div className="chat-users-searchbar-wrapper">
            {showSearch ? (
              <div className="chat-users-searchbar-active">
                <button
                  className="chat-users-searchbar-back"
                  onClick={() => { setShowSearch(false); setSearchQuery(""); }}
                >
                  <i className="bi bi-arrow-left"></i>
                </button>
                <input
                  type="text"
                  className="chat-users-searchbar-input"
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    className="chat-users-searchbar-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            ) : (
              <div
                className="chat-users-searchbar-toggle"
                onClick={() => setShowSearch(true)}
              >
                <i className="bi bi-search me-2"></i>
                <span>Search...</span>
              </div>
            )}
          </div>

          {isCustomer && topBannerSlides.length > 0 && (
            <div className="customer-banner customer-banner-top customer-banner-slider">
              <div
                className="customer-banner-track"
                style={{
                  transform: `translateX(-${topBannerIndex * 100}%)`,
                  transition: topBannerTransitionEnabled ? undefined : "none",
                }}
              >
                {topBannerLoopSlides.map((slide, index) => {
                  const slideUrl = normalizeBannerUrl(slide.link);
                  const slideImage = (
                    <img
                      src={slide.image}
                      alt={`Top banner ${index + 1}`}
                      className="customer-banner-image"
                    />
                  );

                  return (
                    <div key={`${slide.image}-${index}`} className="customer-banner-slide">
                      {slideUrl ? (
                        <a
                          href={slideUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="customer-banner-link"
                        >
                          {slideImage}
                        </a>
                      ) : (
                        slideImage
                      )}
                    </div>
                  );
                })}
              </div>
              {topBannerSlides.length > 1 && (
                <div className="customer-banner-dots">
                  {topBannerSlides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`customer-banner-dot${
                        index === topBannerActiveDot ? " active" : ""
                      }`}
                      onClick={() => {
                        stopTopBannerTimer.current();
                        setTopBannerTransitionEnabled(true);
                        setTopBannerIndex(index);
                        setTimeout(
                          () => startTopBannerTimer.current(),
                          TOP_BANNER_SLIDE_INTERVAL_MS
                        );
                      }}
                      aria-label={`Show slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {!showArchived && !normalizedSearch && archivedCount > 0 && (
            <div
              className={`chat-archived-entry${
                archivedUnreadCount > 0 ? " chat-archived-entry-has-unread" : ""
              }`}
              onClick={openArchivedView}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") openArchivedView();
              }}
            >
              <span className="chat-archived-entry-icon">
                <i className="bi bi-archive"></i>
              </span>
              <span className="chat-archived-entry-label">Archived</span>
              {archivedUnreadCount > 0 && (
                <span
                  className="chat-archived-entry-unread"
                  title={`${archivedUnreadCount} unread`}
                >
                  {archivedUnreadCount}
                </span>
              )}
              <span className="chat-archived-entry-count">{archivedCount}</span>
            </div>
          )}

          {filteredGroups?.length > 0 && (
            <div className="chat-groups-section">
              <h5 className="chat-groups-heading">Groups</h5>
              {filteredGroups.map((group, index) => (
                <Row
                  key={`group-${group?.id || index}`}
                  {...getChatMenuProps({
                    id: group?.id,
                    type: "group",
                    name: group?.name || "Group",
                    archived: isGroupArchived(group),
                    pinned: isGroupPinned(group),
                  })}
                  className={`chat-row-pressable mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer ${
                    isGroupPinned(group) ? "chat-row-pinned " : ""
                  }${hasGroupUnread(group) ? "unread-message-borader" : ""}`}
                >
                  <Col md={2} sm={2} xs={2}>
                    {group?.image ? (
                      <img
                        className="profile-img"
                        src={group.image}
                        alt={group?.name || "Group"}
                        onClick={() => handleGroupOpen(group)}
                      />
                    ) : (
                      <div
                        onClick={() => handleGroupOpen(group)}
                        className="profile-img d-flex align-items-center justify-content-center bg-success text-white fw-bold"
                      >
                        {(group?.name || "G").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Col>
                  <Col
                    md={canManageGroups ? 5 : 8}
                    sm={canManageGroups ? 5 : 8}
                    xs={canManageGroups ? 5 : 8}
                    onClick={() => handleGroupOpen(group)}
                  >
                    <p className="mb-0 fw-bold">{group?.name}</p>
                    <p className="mb-0">
                      <LinkifiedText
                        text={
                          getChatPreviewText(group?.lastMessage) ||
                          group?.topic ||
                          "Tap to start discussion"
                        }
                      />
                    </p>
                  </Col>
                  <Col
                    className="px-0 dddd"
                    md={canManageGroups ? 2 : 2}
                    sm={canManageGroups ? 2 : 2}
                    xs={canManageGroups ? 2 : 2}
                    onClick={() => handleGroupOpen(group)}
                  >
                    <p className="mb-0">
                      <b>
                        {group?.lastMessage?.createdAt &&
                          moment(group?.lastMessage?.createdAt).format("hh:mm A")}
                      </b>
                    </p>
                    {isGroupPinned(group) && (
                      <span className="chat-pin-badge" title="Pinned">
                        <i className="bi bi-pin-angle-fill"></i>
                      </span>
                    )}
                  </Col>
                  {canManageGroups && (
                    <Col md={3} sm={3} xs={3} className="text-end">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className={canDeleteGroup ? "me-2" : ""}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditGroupModal(group);
                        }}
                      >
                        Edit
                      </Button>
                      {canDeleteGroup && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteGroup(group);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </Col>
                  )}
                </Row>
              ))}
              <hr />
            </div>
          )}

          <div className={`chat-tabs-wrapper${showArchived ? " d-none" : ""}`}>
            <div className="chat-tabs-bar">
              {["all", "unread", "read"].map((tab) => (
                <button
                  key={tab}
                  className={`chat-tab-btn${activeTab === tab ? " active" : ""}`}
                  onClick={() => { setActiveTab(tab); setPage(1); }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : filteredUsers?.length > 0 ? (
            <>
              {filteredUsers.map((item, index) => (
                <Row
                  key={`user-${item?.userId || item?.sender || index}`}
                  onClick={() => {
                    saveNavState();
                    navigate(
                      `${chat}?id=${
                        item?.userId ? item?.userId : item?.sender
                      }&name=${
                        item?.userId
                          ? item?.username
                            ? item?.username
                            : `${item?.firstName} ${item?.lastName}`
                          : "New User"
                      }&img=${item?.userId ? item?.profile : ""}&time=${
                        item?.userId ? item?.lastSeen : ""
                      }&agent=${agentList ? agentList : ""}&roleSlug=${item?.roleSlug || ""}`
                    );
                  }}
                  className={`mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer ${
                         item?.lastMessage && !item.lastMessage.isRead && item?.lastMessage?.sender !== localStorage.getItem("userid")
                            ? `unread-message-borader` : ``
                        }`}
                >
                  <Col md={2} sm={2} xs={2}>
                    <div className="profile-img-wrapper">
                      <img
                        className="profile-img"
                        src={item?.profile ? item?.profile : logo}
                        alt={item?.firstName || "user"}
                        style={!item?.profile ? { objectFit: "contain", background: "#fff" } : undefined}
                      />
                      {(item?.roleSlug === "team_leader" || item?.roleSlug === "branch_leader") && (
                        <span className="verified-badge">
                          <i className="bi bi-patch-check-fill"></i>
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">
                      {!item?.username
                        ? `New User`
                        : item?.username
                        ? item?.username
                        : `${item?.firstName} ${item?.lastName}`}
                    </p>
                    {!item?.username ? (
                      <p className="mb-0">
                        <LinkifiedText
                          text={getChatPreviewText(item)}
                        />
                      </p>
                    ) : (
                      getChatPreviewText(item?.lastMessage) && (
                        <p className="mb-0">
                          <LinkifiedText
                            text={getChatPreviewText(item?.lastMessage)}
                          />
                        </p>
                      )
                    )}
                  </Col>
                  {item?.userId !== localStorage.getItem("userid") && (
                    <Col className={`px-0 dddd`} md={2} sm={2} xs={2}>
                      <p
                        className={`mb-0 ${
                          !item?.lastMessage?.isRead && item?.lastMessage?.sender !== localStorage.getItem("userid")
                            ? `text-success` : ``
                        }`}
                      >
                        <b>
                          {item?.lastMessage?.createdAt &&
                            moment(item?.lastMessage?.createdAt).format(
                              "hh:mm A"
                            )}
                        </b>
                      </p>
                    </Col>
                  )}
                </Row>
              ))}
              {!isCustomer && totalPages > 1 && (() => {
                const delta = 2;
                const pages = [];
                for (let i = 1; i <= totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
                    pages.push(i);
                  }
                }
                const buttons = [];
                let prev = null;
                for (const p of pages) {
                  if (prev !== null && p - prev > 1) {
                    buttons.push(<span key={`ellipsis-${p}`} className="chat-page-ellipsis">…</span>);
                  }
                  buttons.push(
                    <button
                      key={p}
                      className={`chat-page-btn${page === p ? " active" : ""}`}
                      onClick={() => setPage(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  );
                  prev = p;
                }
                return (
                  <Row className="mt-3 mb-2">
                    <Col className="d-flex justify-content-center">
                      <div className="chat-pagination">
                        <button className="chat-page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>
                          <i className="bi bi-chevron-left"></i>
                        </button>
                        {buttons}
                        <button className="chat-page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </Col>
                  </Row>
                );
              })()}
            </>
          ) : showArchived ? (
            filteredGroups.length === 0 && (
              <div className="text-center mt-5">
                <h4 className="text-muted w-100">No archived chats.</h4>
                <p className="text-muted">
                  Long press a chat to archive it.
                </p>
              </div>
            )
          ) : !normalizedSearch ? (
            <div className="text-center mt-5 justify-content-center align-items-center">
              {activeTab !== "all" ? (
                <h4 className="text-muted w-100">No {activeTab} chats.</h4>
              ) : (
                <>
                  <h4 className="text-danger w-100">No users found to chat with.</h4>
                  <h4 className="text-success w-100">
                    Some time need to refresh{" "}
                    <Button onClick={() => window.location.reload()}>Click</Button>
                  </h4>
                </>
              )}
            </div>
          ) : null}

          {/* DB message matches section */}
          {normalizedSearch && (dbSearchLoading || hasMessageMatches) && (
            <div className="chat-users-msg-matches">
              <h6 className="chat-users-msg-matches-heading">Messages</h6>
              {dbSearchLoading && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {msgMatchGroups.map((match, index) => (
                <Row
                  key={`msgmatch-group-${match.groupId}-${index}`}
                  className="mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer"
                  onClick={() => { saveNavState(); navigate(`${chat}?group=true&groupId=${match.groupId}&name=${encodeURIComponent(match.name)}&topic=&searchQ=${encodeURIComponent(searchQuery)}`); }}
                >
                  <Col md={2} sm={2} xs={2}>
                    {match.image ? (
                      <img className="profile-img" src={match.image} alt={match.name} />
                    ) : (
                      <div className="profile-img d-flex align-items-center justify-content-center bg-success text-white fw-bold">
                        {(match.name || "G").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">{match.name}</p>
                    <p className="mb-0 chat-users-match-snippet">
                      <LinkifiedText text={match.latestContent} highlight={searchQuery} />
                    </p>
                  </Col>
                  <Col md={2} sm={2} xs={2} className="px-0 text-end">
                    <small className="text-muted">{match.matchCount} match{match.matchCount !== 1 ? "es" : ""}</small>
                  </Col>
                </Row>
              ))}
              {msgMatchUsers.map((match, index) => (
                <Row
                  key={`msgmatch-user-${match.userId}-${index}`}
                  className="mt-2 shadow-none py-2 bg-light-gray rounded align-items-center cursor-pointer"
                  onClick={() => { saveNavState(); navigate(`${chat}?id=${match.userId}&name=${encodeURIComponent(match.username || "User")}&img=${encodeURIComponent(match.profile || "")}&agent=${agentList || ""}&searchQ=${encodeURIComponent(searchQuery)}`); }}
                >
                  <Col md={2} sm={2} xs={2}>
                    <img
                      className="profile-img"
                      src={match.profile || logo}
                      alt={match.username || "User"}
                      style={!match.profile ? { objectFit: "contain", background: "#fff" } : undefined}
                    />
                  </Col>
                  <Col md={8} sm={8} xs={8}>
                    <p className="mb-0 fw-bold">{match.username || "User"}</p>
                    <p className="mb-0 chat-users-match-snippet">
                      <LinkifiedText text={match.latestContent} highlight={searchQuery} />
                    </p>
                  </Col>
                  <Col md={2} sm={2} xs={2} className="px-0 text-end">
                    <small className="text-muted">{match.matchCount} match{match.matchCount !== 1 ? "es" : ""}</small>
                  </Col>
                </Row>
              ))}
            </div>
          )}

          {/* No results at all */}
          {normalizedSearch && !loading && !dbSearchLoading && !hasAnyResults && (
            <div className="text-center mt-4">
              <p className="text-muted">No results for "{searchQuery}"</p>
            </div>
          )}
          {isCustomer && customerBannerSettings?.customer_bottom_banner_text && (
            <div className="customer-banner customer-banner-bottom">
              {bottomBannerUrl ? (
                <a
                  href={bottomBannerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="customer-banner-link"
                >
                  <img
                    src={customerBannerSettings.customer_bottom_banner_text}
                    alt="Bottom banner"
                    className="customer-banner-image"
                  />
                </a>
              ) : (
                <img
                  src={customerBannerSettings.customer_bottom_banner_text}
                  alt="Bottom banner"
                  className="customer-banner-image"
                />
              )}
            </div>
          )}
        </div>
      </div>
      {isCustomer && supportLinkUrl && (
        <a
          href={supportLinkUrl}
          target="_blank"
          rel="noreferrer"
          className="customer-support-fab"
          aria-label="24/7 customer support"
        >
          <img src={supportIcon} alt="24/7 customer care" />
        </a>
      )}
      <Footer />

      {chatMenu && (
        <>
          <div className="chat-context-backdrop" onClick={closeChatMenu} />
          <div
            className="chat-context-menu"
            style={{ left: chatMenu.left, top: chatMenu.top }}
          >
            <button
              type="button"
              className="chat-context-menu-item"
              onClick={() => togglePin(chatMenu)}
            >
              <i
                className={`bi ${
                  chatMenu.pinned ? "bi-pin-angle" : "bi-pin-angle-fill"
                } me-2`}
              ></i>
              {chatMenu.pinned ? "Unpin group" : "Pin group"}
            </button>
            <button
              type="button"
              className="chat-context-menu-item"
              onClick={() => toggleArchive(chatMenu)}
            >
              <i
                className={`bi ${
                  chatMenu.archived ? "bi-box-arrow-up" : "bi-archive"
                } me-2`}
              ></i>
              {chatMenu.archived ? "Unarchive" : "Archive"}
            </button>
          </div>
        </>
      )}

      <Modal show={showGroupModal} onHide={closeGroupModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroupId ? "Edit Group" : "Create Group"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder="Enter group name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Topic</Form.Label>
            <Form.Control
              value={groupTopic}
              onChange={(event) => setGroupTopic(event.target.value)}
              placeholder="Enter discussion topic"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="group-image">Group DP</Form.Label>
            <Form.Control
              id="group-image"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const selectedFile = event.target.files[0];
                setGroupImage(selectedFile || null);
                setGroupImagePreview(
                  selectedFile ? URL.createObjectURL(selectedFile) : editingGroupId ? groupImagePreview : ""
                );
              }}
            />
            {groupImagePreview && (
              <img
                src={groupImagePreview}
                alt="Group preview"
                className="profile-img mt-2"
              />
            )}
          </Form.Group>

          <Form.Label>Select Members</Form.Label>
          {groupLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="chat-forward-users">
              {groupUsers.map((user) => {
                const fullName =
                  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                  "No Name";

                return (
                  <label key={user?.id} className="chat-forward-user">
                    <img
                      className="profile-img"
                      src={user?.image || logo}
                      alt={fullName}
                      style={!user?.image ? { objectFit: "contain", background: "#fff" } : undefined}
                    />
                    <div className="chat-forward-user-copy">
                      <strong>{fullName}</strong>
                    </div>
                    <Form.Check
                      type="checkbox"
                      checked={selectedMembers.includes(user?.id)}
                      onChange={() => toggleGroupMember(user?.id)}
                    />
                  </label>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeGroupModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={saveGroup}
            disabled={creatingGroup || !groupName.trim()}
          >
            {creatingGroup
              ? editingGroupId
                ? "Saving..."
                : "Creating..."
              : editingGroupId
              ? "Save Changes"
              : "Create Group"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChatUsers;
